import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import classNames from 'classnames';
import _ from 'lodash';

import {
  CATEGORY_SEARCH_PARAM,
  SORT_SEARCH_PARAM,
  FILTER_SEARCH_PARAMS,
  REWARD_ID_ROUTE_PARAM,
  ROUTE_REWARDS,
  ROUTE_REWARDS_STORE_REWARD_ID,
  STORE_ID_ROUTE_PARAM,
} from '../../config/routes';
import Tabs from '../../global/Mui/Tabs';
import Tab from '../../global/Mui/Tab';
import Sorting from '../../global/Sorting';
import LoadingBar from '../../global/LoadingBar';
import { getCurrentRouteTitle, getSearchParam } from '../../utils/routes';
import { getCategories, getRewards, getConfigurations } from '../../utils/service';
import { getGenericError } from '../../utils/errors';
import useRewardsV2Flag from '../../utils/hooks/useRewardsV2Flag';
import { isProd } from '../../../../config/config';
import { LIST_PAGE_SIZE } from '../../config/config';
import SEARCH_TYPE from '../../utils/searchType';
import Filter from './Filter';
import CategoryTabs from '../../global/Mui/CategoryTabs';
import CategoryTab from '../../global/Mui/CategoryTab';
import {
  FIXED_CATEGORIES,
  FLAGS,
  getCategory,
  getFilter,
  getFilterParams,
  getCategoryParam,
  getSearchTextParam,
} from './utils';
import useNavigateWithSearch from '../../../../hooks/useNavigateWithSearch';
import {
  getSort,
  getSortingParam,
  sortingOptions,
} from '../../utils/sorting';
import { REWARD_PREVIEW_TYPES } from '../../config/rewards';

import RewardsSearch from '../SearchBar/RewardsSearch/RewardsSearch';
import RewardsResults, { RESULTS_TYPE } from '../SearchBar/RewardsResults';
import SearchBar from '../SearchBar';

import './Rewards.scss';
import FilterV2 from '../RewardsV2/FilterV2';

const LIST_ELEMENT_ID = 'List';
const DEFAULT_ICON_BACKGROUND = 'var(--trashie-ui-lavander-pastille)';

const Rewards = () => {
  const rewardsV2Enabled = useRewardsV2Flag();
  const { search } = useLocation();
  const navigateWithSearch = useNavigateWithSearch();
  const timeoutRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [categoryRewards, setCategoryRewards] = useState([]);
  const [categoryRewardsTotal, setCategoryRewardsTotal] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRewards, setIsLoadingRewards] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [rewardsError, setRewardsError] = useState('');
  const [hasLoadedRewards, setHasLoadedRewards] = useState(false);
  const [sortingSelected, setSortingSelected] = useState(_.first(sortingOptions));
  const [isCheking, setIsCheking] = useState(false);
  const [filterSelected, setFilterSelected] = useState({});
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [rewardProperties, setRewardProperties] = useState({});
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchedText, setSearchedText] = useState('');

  const query = useMemo(() => (
    new URLSearchParams(search)
  ), [search]);

  const {
    rewardCategoryParam,
    rewardSortParam,
    rewardFilterTypeParam,
    rewardFilterMinValueParam,
    rewardFilterMaxValueParam,
    rewardFilterIsNewParam,
  } = useMemo(() => ({
    rewardCategoryParam: getSearchParam(query, CATEGORY_SEARCH_PARAM),
    rewardSortParam: getSearchParam(query, SORT_SEARCH_PARAM),
    rewardFilterMinValueParam: getSearchParam(query, FILTER_SEARCH_PARAMS.MIN_VALUE),
    rewardFilterMaxValueParam: getSearchParam(query, FILTER_SEARCH_PARAMS.MAX_VALUE),
    rewardFilterTypeParam: getSearchParam(query, FILTER_SEARCH_PARAMS.TYPE),
    rewardFilterIsNewParam: getSearchParam(query, FILTER_SEARCH_PARAMS.IS_NEW),
  }), [query]);

  const scrollToTop = useCallback(() => {
    const listElement = document.getElementById(LIST_ELEMENT_ID);

    if (listElement) {
      timeoutRef.current = setTimeout(() => {
        listElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }, []);

  const validateForYouRewards = useCallback(async () => {
    const {
      data,
    } = await getRewards({
      pageSize: LIST_PAGE_SIZE,
      type: REWARD_PREVIEW_TYPES.EXCLUSIVE,
      page: 1,
      exchangeValueMin: 1,
      categoryId: FLAGS.IS_EXCLUSIVE,
    });

    if (data.total <= 0) {
      _.remove(FIXED_CATEGORIES, (category) => category.uid === FLAGS.IS_EXCLUSIVE);
    }
  }, []);

  const validateEndingSoonRewards = useCallback(async () => {
    const {
      data,
    } = await getRewards({
      pageSize: 1,
      type: REWARD_PREVIEW_TYPES.ENDING_SOON,
      page: 1,
      exchangeValueMin: 1,
      isEndingIn: rewardProperties?.ending?.endingDays || 7,
    });

    if (data.total <= 0) {
      _.remove(FIXED_CATEGORIES, (category) => category.uid === FLAGS.ENDING_SOON);
      console.log('FIXED_CATEGORIES', FIXED_CATEGORIES);
    }
    console.log('data', data);
  }, []);

  const loadCategoryRewards = useCallback(async (
    routeName,
    sorting,
    filter,
    newCategories,
    searchText,
  ) => {
    setIsLoadingRewards(true);

    const newPage = ((
      routeName === selectedCategory.routeName &&
      sorting.id === sortingSelected.id &&
      _.isEqual(filter, filterSelected)
    ) || (isSearching && searchText === searchedText))
      ? page + 1
      : 1;

    if (newPage === 1) {
      setCategoryRewards([]);
      setCategoryRewardsTotal(0);
    }

    const currentTotal = newPage === 1 ? 0 : categoryRewardsTotal;
    const currentCategoryRewards = newPage === 1 ? [] : [...categoryRewards];
    const category = getCategory(newCategories?.length ? newCategories : categories, routeName);
    const updatedFilter = { ...filter };

    if (!Object.prototype.hasOwnProperty.call(filter, 'minValue')) {
      updatedFilter.minValue = 1;
    }

    const {
      data,
      error: categoryRewardsError,
    } = await getRewards({
      pageSize: LIST_PAGE_SIZE,
      type: category.uid === FLAGS.IS_EXCLUSIVE ? REWARD_PREVIEW_TYPES.EXCLUSIVE : undefined,
      page: newPage,
      ...getCategoryParam(category.uid, rewardProperties?.ending?.endingDays),
      ...getSortingParam(sorting),
      ...getFilterParams(updatedFilter),
      ...getSearchTextParam(searchText),
    });

    if (categoryRewardsError) {
      setCategoryRewards(currentCategoryRewards);
      setCategoryRewardsTotal(currentTotal);
      setPage(page);
      setRewardsError(categoryRewardsError.message || getGenericError());
      setIsLoadingRewards(false);
      return;
    }

    setCategoryRewards([...currentCategoryRewards, ...data.rewards]);
    setCategoryRewardsTotal(data.total);
    setPage(newPage);
    setRewardsError('');
    setIsLoadingRewards(false);
    setHasLoadedRewards(true);
  }, [
    selectedCategory,
    sortingSelected,
    filterSelected,
    page,
    categories,
    categoryRewardsTotal,
    categoryRewards,
  ]);

  const loadCategories = useCallback(async () => {
    setIsLoading(true);

    const {
      data,
      error: rewardCategoriesError,
    } = await getCategories({ minExchangeValue: 1 });

    if (rewardCategoriesError) {
      setCategories([]);
      setSelectedCategory({});
      setError(rewardCategoriesError.message || getGenericError());
      setIsLoading(false);
      return;
    }

    await validateForYouRewards();
    await validateEndingSoonRewards();

    const newCategories = [
      ...FIXED_CATEGORIES,
      ...data.categories,
    ];
    const initialSelectedCategory = getCategory(newCategories, rewardCategoryParam);
    const initialSort = getSort(rewardSortParam);
    const initialFilter = getFilter({
      type: rewardFilterTypeParam,
      minValue: rewardFilterMinValueParam,
      maxValue: rewardFilterMaxValueParam,
      other: {
        [FLAGS.IS_NEW]: rewardFilterIsNewParam,
      },
    });

    setCategories(newCategories);
    setSelectedCategory(initialSelectedCategory);
    setSortingSelected(initialSort);
    setFilterSelected(initialFilter);
    setError('');
    setIsLoading(false);
    loadCategoryRewards(
      initialSelectedCategory.routeName,
      initialSort,
      initialFilter,
      newCategories,
    );

    navigateWithSearch(ROUTE_REWARDS, {
      [CATEGORY_SEARCH_PARAM]: initialSelectedCategory.routeName,
      [SORT_SEARCH_PARAM]: initialSort.id,
      [FILTER_SEARCH_PARAMS.MIN_VALUE]: initialFilter.minValue || null,
      [FILTER_SEARCH_PARAMS.MAX_VALUE]: initialFilter.maxValue || null,
      [FILTER_SEARCH_PARAMS.TYPE]: initialFilter.type || null,
      [FILTER_SEARCH_PARAMS.IS_NEW]: initialFilter.other?.[FLAGS.IS_NEW] || null,
    });
  }, [
    rewardCategoryParam,
    rewardSortParam,
    rewardFilterTypeParam,
    rewardFilterMinValueParam,
    rewardFilterMaxValueParam,
    rewardFilterIsNewParam,
    loadCategoryRewards,
    validateForYouRewards,
    validateEndingSoonRewards,
    navigateWithSearch,
  ]);

  const rewardsConfig = useCallback(async () => {
    setIsLoadingConfig(true);
    const { value, error: configError } = await getConfigurations('rewardProperties');
    if (!configError) {
      setRewardProperties(value);
    }
    setIsLoadingConfig(false);
  }, []);

  const handleCategoryChange = useCallback((event, newValue) => {
    if (newValue === selectedCategory.routeName) {
      return;
    }

    const newCategory = getCategory(categories, newValue);
    setCategoryRewards([]);
    setSelectedCategory(newCategory);
    loadCategoryRewards(newCategory.routeName, sortingSelected, filterSelected);
    scrollToTop();

    if (event.target) {
      navigateWithSearch(ROUTE_REWARDS, {
        [CATEGORY_SEARCH_PARAM]: newValue,
      });
    }
  }, [
    navigateWithSearch,
    selectedCategory,
    sortingSelected,
    filterSelected,
    categories,
    loadCategoryRewards,
  ]);

  const handleOnClick = useCallback((rewardItem) => {
    const relativePath = ROUTE_REWARDS_STORE_REWARD_ID
      .replace(STORE_ID_ROUTE_PARAM, rewardItem.store?.id)
      .replace(REWARD_ID_ROUTE_PARAM, rewardItem.uid);

    navigateWithSearch(relativePath);
  }, [navigateWithSearch]);

  const handleSortingChange = useCallback((newValue) => {
    setSortingSelected(newValue);
    setCategoryRewards([]);
    loadCategoryRewards(selectedCategory.routeName, newValue, filterSelected);
    scrollToTop();

    navigateWithSearch(ROUTE_REWARDS, {
      [SORT_SEARCH_PARAM]: newValue.id,
    });
  }, [selectedCategory, filterSelected, navigateWithSearch, loadCategoryRewards]);

  const handleFilterCheck = useCallback(async ({
    minValue = 1,
    maxValue,
    type,
    other,
  }) => {
    if (!hasLoadedRewards) {
      return false;
    }

    setIsCheking(true);

    const category = getCategory(categories, selectedCategory.routeName);

    const {
      data,
      error: categoryRewardsError,
    } = await getRewards({
      pageSize: 1,
      page: 1,
      type: category.uid === FLAGS.IS_EXCLUSIVE ? REWARD_PREVIEW_TYPES.EXCLUSIVE : undefined,
      ...getCategoryParam(category.uid, rewardProperties?.ending?.endingDays),
      ...getSortingParam(sortingSelected),
      ...getFilterParams({
        minValue,
        maxValue,
        type,
        other,
      }),
    });

    if (categoryRewardsError) {
      setIsCheking(false);
      return false;
    }

    setIsCheking(false);

    return Boolean(data.total);
  }, [hasLoadedRewards, categories, selectedCategory, sortingSelected]);

  const handleFilterChange = useCallback((newFilter) => {
    setFilterSelected(newFilter);
    loadCategoryRewards(selectedCategory.routeName, sortingSelected, newFilter);

    navigateWithSearch(ROUTE_REWARDS, {
      [FILTER_SEARCH_PARAMS.MIN_VALUE]: newFilter.minValue || null,
      [FILTER_SEARCH_PARAMS.MAX_VALUE]: newFilter.maxValue || null,
      [FILTER_SEARCH_PARAMS.TYPE]: newFilter.type || null,
      [FILTER_SEARCH_PARAMS.IS_NEW]: newFilter.other?.[FLAGS.IS_NEW] || null,
    });
  }, [selectedCategory, sortingSelected, loadCategoryRewards, navigateWithSearch]);

  useEffect(() => {
    if (hasLoadedRewards && rewardCategoryParam) {
      handleCategoryChange({}, rewardCategoryParam);
    }
  }, [hasLoadedRewards, rewardCategoryParam]);

  useEffect(() => {
    loadCategories();
    rewardsConfig();

    return () => clearTimeout(timeoutRef.current);
  }, []);

  if (isSearching) {
    return (
      <RewardsSearch
        loadCategories={loadCategories}
        setIsSearching={setIsSearching}
        loadCategoryRewards={loadCategoryRewards}
        handleOnClick={handleOnClick}
        rewardProperties={rewardProperties}
        isLoadingRewards={isLoadingRewards}
        isLoadingConfig={isLoadingConfig}
        categoryRewards={categoryRewards}
        categoryRewardsTotal={categoryRewardsTotal}
        selectedCategory={selectedCategory?.routeName}
        sortingSelected={sortingSelected}
        filterSelected={filterSelected}
        rewardsError={rewardsError}
        listElementId={LIST_ELEMENT_ID}
        setSearchedText={setSearchedText}
        searchedText={searchedText}
      />
    );
  }

  return (
    <div className="RewardsView">
      <div className="RewardsView__content">
        {error || (isLoading ? (
          <LoadingBar className="RewardsView__content--loader" />
        ) : (
          <>
            <Box className={classNames('RewardsView__content--controls', { hasEnvBanner: !isProd() })}>
              <Box className="RewardsView__content--controls-filter-group">
                <div className="RewardsView__content--header">
                  <div>
                    <h2 className="RewardsView__content--header-title">
                      {getCurrentRouteTitle()}
                    </h2>
                    <p className="RewardsView__content--header-subtitle">
                      Earn TrashieCash™ back when using rewards
                    </p>
                  </div>
                  <Box className="RewardsView__content--controls-options">
                    <SearchBar
                      searchType={SEARCH_TYPE.REWARDS}
                    />
                    {rewardsV2Enabled ? (
                      <FilterV2
                        filter={filterSelected}
                        onSubmit={handleFilterChange}
                        onCheck={handleFilterCheck}
                        isCheking={isCheking}
                        open={isOpenFilter}
                        onOpenChange={setIsOpenFilter}
                        sortingOptions={sortingOptions}
                        sortingOptionSelected={sortingSelected}
                        onChange={handleSortingChange}
                      />
                    ) : (
                      <>
                        <Filter
                          filter={filterSelected}
                          onSubmit={handleFilterChange}
                          onCheck={handleFilterCheck}
                          isCheking={isCheking}
                          open={isOpenFilter}
                          onOpenChange={setIsOpenFilter}
                        />
                        <Sorting
                          sortingOptions={sortingOptions}
                          sortingOptionSelected={sortingSelected}
                          onChange={handleSortingChange}
                        />

                      </>
                    ) }
                  </Box>
                </div>
              </Box>
              {rewardsV2Enabled ? (
                <CategoryTabs
                  className="RewardsView__content--controls-tabsV2"
                  value={selectedCategory.routeName}
                  onChange={handleCategoryChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="rewards categories"
                >
                  {categories.map(({ uid, name, iconUrl, routeName, meta }) => (
                    <CategoryTab
                      key={uid}
                      value={routeName}
                      label={name}
                      iconBackground={meta?.bgColor || DEFAULT_ICON_BACKGROUND}
                      icon={<img src={iconUrl} alt="tab icon" />}
                    />
                  ))}
                </CategoryTabs>
              ) : (
                <Tabs
                  className="RewardsView__content--controls-tabs"
                  value={selectedCategory?.routeName}
                  onChange={handleCategoryChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="rewards categories"
                  withFilters={false}
                >
                  {categories.map(({ uid, name, iconUrl, routeName }) => (
                    <Tab key={uid} value={routeName} label={name} icon={<img src={iconUrl} alt="tab icon" />} />
                  ))}
                </Tabs>
              )}

            </Box>
            <RewardsResults
              selectedCategoryId={selectedCategory?.uid}
              resultsType={RESULTS_TYPE.REWARDS_LIST}
              listElementId={LIST_ELEMENT_ID}
              isLoadingRewards={isLoadingRewards}
              isLoadingConfig={isLoadingConfig}
              categoryRewards={categoryRewards}
              rewardProperties={rewardProperties}
              loadCategoryRewards={loadCategoryRewards}
              selectedCategory={selectedCategory?.routeName}
              sortingSelected={sortingSelected}
              filterSelected={filterSelected}
              categoryRewardsTotal={categoryRewardsTotal}
              handleOnClick={handleOnClick}
              rewardsError={rewardsError}
              noResultsTitle="No rewards found"
              noResultsSubtitle="Tweak the filters to view available rewards."
              noResultsCtaText="Tweak Filters"
              noResultsCtaOnClick={() => setIsOpenFilter(true)}
              searchedText={searchedText}
            />
          </>
        ))}
      </div>
    </div>
  );
};

export default Rewards;
